import { Inject, Injectable, NgZone } from '@angular/core';
import { sqlite } from '../../environments/sqlite';
import { DataSource, MoreThanOrEqual, QueryFailedError, TypeORMError } from 'typeorm';
import { NGXLogger } from 'ngx-logger';
import { DOCUMENT } from '@angular/common';
import {
  ContactElement,
  ContactInformation,
  Hospital,
  InformationCategory,
  InformationPage,
  InformationPageImage,
  MediaFile, Moment, News, Phase, Topic
} from '@nsk-app/models';
import { ApiService } from '@nsk-app/services/api.service';
import { from, Observable, ReplaySubject, Subject, timer } from 'rxjs';
import { ConfigurationService } from '@nsk-app/services/configuration.service';
import { switchMap } from 'rxjs/operators';
import { MediaCacheService } from '@nsk-app/services/media-cache.service';
import { LoadingController } from '@ionic/angular';
import { AnalyticsService } from '@nsk-app/services/analytics.service';
import { AddNewsTable1635258789259 } from '@nsk-app/migrations/AddNewsTable1635258789259';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class DatabaseService {

  private database: DataSource;
  private informationData: Subject<InformationCategory[]> = new ReplaySubject<InformationCategory[]>(1);
  private contactData: Subject<ContactInformation> = new ReplaySubject<ContactInformation>(1);
  private isLoadingNews: boolean = false;

  public readonly newDataObservable: Subject<void> = new ReplaySubject<void>(1);
  public readonly newsObservable: Subject<News[]> = new ReplaySubject<News[]>(1);

  constructor(
    private logger: NGXLogger,
    private api: ApiService,
    private cache: MediaCacheService,
    private loadingController: LoadingController,
    private configuration: ConfigurationService,
    private analytics: AnalyticsService,
    private router: Router,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) { }

  async deleteDatabase(): Promise<void> {

    try{
      await this.database.dropDatabase();
    }catch (e){
      this.logger.warn('Could not drop old database');
    }
    // await this.database.synchronize();
    await this.persistDatabase();

    this.configuration.hasDatabase = null;
    await this.configuration.saveConfiguration();
  }

  async initializeDatabase(): Promise<DataSource> {
    this.logger.debug('Initializing Database.');

    // if(environment.production == false && Capacitor.getPlatform() !== 'web'){
    //   await new Promise<void>(res => {
    //
    //     const i = setInterval(()=>{
    //       if(window['continue'] === true){
    //         resolve();
    //       }
    //     },100);
    //
    //     function resolve(){
    //       clearInterval(i);
    //       res();
    //     }
    //
    //   });
    // }


    if (!this.database) {
      this.logger.debug('No Database, creating new');


      const entities = [
        Hospital,
        MediaFile,
        InformationCategory,
        InformationPage,
        InformationPageImage,
        ContactInformation,
        ContactElement,
        Phase,
        Topic,
        Moment,
        News
      ];

      const migrations = [
        AddNewsTable1635258789259
      ];


      try {
        this.logger.debug('Opening connection');

        this.database = await sqlite.initDataSource();

      } catch (e){
        this.logger.fatal('Could not init data source!', {e});
      }

      try {

        await this.database.initialize();
      } catch (e){
        this.logger.fatal('Could not init database Connection!', {e});
      }

      try {

        this.logger.debug('Running migrations...');
        await this.database.runMigrations();

      } catch (e){
        this.logger.fatal('Could not run migrations!', {e});
      }

      try {
        await this.database.synchronize(false);
      } catch (e){
        this.logger.fatal('Could not save database!', {e});
      }

      try {

        this.logger.debug('Service ready.', this);

        this.logger.debug('Database loaded.', this.database);

        this.newDataObservable.subscribe(async () => {
          await Promise.all([
            this.cacheInformationCategories(),
            this.cacheContactData()
          ]);
        });
      }catch (e){
        this.logger.fatal('Could not subscribe to data!', {e});
      }
    }else{
      this.logger.warn('Database was already initialized!', this.database);

    }

    if(this.configuration.hasDatabase != null){
      this.newsObservable.next(await News.find());

      PushNotifications.addListener('pushNotificationReceived', (n) => {
        this.logger.info('Detected incoming notification!',n);
        this.loadNews();
      });

      PushNotifications.addListener('pushNotificationActionPerformed', async (n: ActionPerformed) => {
        this.logger.info('Detected push action!',n);
        this.logger.info('Navigate to news tab...');
        this.loadNews();

        setTimeout(() => this.ngZone.run(() => {
          this.router.navigate(['/main/news'],{skipLocationChange: true});
        }),500);
      });

      this.logger.info('Checking for updates...');
      try{
        const date = await this.api.getLastUpdate();

        if(date > this.configuration.hasDatabase){
          this.logger.info('Syncing...');
          await this.synchronize(this.configuration.topic.id);
        }else{
          this.logger.info('Everything up to date!',{lastUpdate: date, lastSync: this.configuration.hasDatabase});
          this.newDataObservable.next();

        }

        this.analytics.submitEvents();

      }catch (e){
        this.logger.warn('Could not check for update.',e);
        this.newDataObservable.next();

      }

    }

    return this.database;
  }

  async persistDatabase(): Promise<void> {
    // await this.database.synchronize();

  }

  async showLoading() {
    const loading = await this.loadingController.create({
      message: 'Lade Daten...',
    });

    await loading.present();

    return loading;
  }

  async synchronize(topicId: number): Promise<void> {
    const loading = await this.showLoading();
    const data = await this.api.getTopicDatabase(topicId).toPromise();

    this.configuration.hasDatabase = null;
    await this.configuration.saveConfiguration();

    await this.database.synchronize(true);
    await this.persistDatabase();

    this.logger.debug('Got data from api.', data);

    try{

      let res = await this.database.query('PRAGMA foreign_keys = OFF');
      this.logger.debug('Turning OFF foreign keys...',res);
      await this.database.synchronize();
      res = await this.database.query('PRAGMA foreign_keys = OFF');
      this.logger.debug('Turning OFF (again) foreign keys...',res);

      // TODO mehr cascading relationships
      await Promise.all([
        MediaFile.save(data.media),
        Hospital.save(data.hospitals),
        Topic.save(data.topics),
        ContactInformation.save(data.contacts),
        InformationCategory.save(data.information_categories),
        InformationPage.save(data.information_pages),
        InformationPageImage.save(data.information_page_images),
        Phase.save(data.phases),
        Moment.save(data.moments),
    ]);

      await this.loadNews();
      await this.database.query('PRAGMA foreign_keys = ON');
      await this.database.synchronize();


      await this.persistDatabase();

      this.configuration.hasDatabase = new Date();
      await this.configuration.saveConfiguration();

      this.logger.debug('Saved data');

      // await this.cache.clear();
      const urls = data.media.map(media => media.url)
        .concat(data.moments.map(moment => moment.media_url))
        .concat(data.media
          .filter( x => x.url.endsWith('mp4'))
          .map(media => media.thumb)
        )
        .filter(x => x != null && x.startsWith('http'));

      try{
        await this.cache.cacheFiles([...new Set(urls)]);
        this.logger.debug('Cached files');
      }catch (e){
        this.logger.error('Could not cache files');
      }

      this.newDataObservable.next();

      await this.analytics.updateTracking();

    }catch (e){
      this.logger.fatal('Could not sync data with api!', e.stack);
      this.configuration.hasDatabase = null;

      await this.configuration.saveConfiguration();
    }

    timer(1000).toPromise().then(() => {
      loading.dismiss();
    });
    // this.dumpDatabase();
  }

  dumpDatabase(): void {
    const raw: number[] = JSON.parse(localStorage.getItem('nsk'));
    const buf = new Uint8Array(raw);
    const blob = new Blob([buf], {type: 'application/x-sqlite3'});
    const link: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
    link.href = URL.createObjectURL(blob);
    link.download = 'database_export.sqlite';
    link.click();

    setTimeout(() => {

      const url = link.href;
      URL.revokeObjectURL(url);
      link.remove();
    }, 1000);
  }

  async loadCategoryPages(category: InformationCategory): Promise<InformationPage[]> {
    return InformationPage.find({
      where: {
        information_category_id: category.id,
        parent_id: null
      }, order: {
        order: 'ASC'
      }
    });
  }

  async cacheContactData(): Promise<void> {
    const topic = await Topic.findOne({where:{id: this.configuration.topic.id}, relations: ['contact', 'contact.elements', 'contact.image']});
    this.contactData.next(topic.contact);
    this.logger.log("Caching contact data.");
  }

  async cacheInformationCategories(): Promise<void> {
    const repo = this.database.getTreeRepository<InformationCategory>(InformationCategory);

    const roots = await repo.findTrees();
    await Promise.all(roots.map(async root => {

      root.pages = await this.loadCategoryPages(root);


      await Promise.all(root.children.map(async subCat => {

        subCat.pages = await this.loadCategoryPages(subCat);

        return subCat;
      }));

      return root;
    }));

    this.logger.debug('Cached InformationCategory Tree');
    this.informationData.next(roots);
  }

  async loadNews(): Promise<void> {

    if(this.isLoadingNews){
      this.logger.info('Already loading news, waiting for data....');
      await this.newsObservable.toPromise();
      return;
    }

    this.isLoadingNews = true;

    let news: News[] = [];

    try{
      this.logger.info('Loading news from Server');
      news = (await this.api.getNews(this.configuration.topic.id).toPromise());

      this.logger.info('Clearing old news...');
      await News.clear();

      this.logger.info('Saving new news...',news);
      await News.save(news);

      let urls = news
        .filter(n => n.image != null)
        .map(n => n.image);

      await this.cache.cacheFiles(urls);
    }catch (e) {
      this.logger.error('Could not fetch News', {ex:e});

      if(e.__proto__.toString() === 'QueryFailedError'){
        let error = e as QueryFailedError;
        this.logger.error(error.query);
      }
    }

    await this.persistDatabase();

    this.isLoadingNews = false;
    this.newsObservable.next(await News.find({order: {created_at: 'DESC'}}));
  }

  getInformationCategories(): Observable<InformationCategory[]> {
    return this.informationData;
  }

  getContactData(): Observable<ContactInformation> {
    return this.contactData;
  }

  getInformationPage(pageId: number): Observable<InformationPage> {
    return from(InformationPage.findOne({where: {id: pageId}, relations: ['children']}));
  }

  getTopic(): Observable<Topic> {

    return this.newDataObservable.pipe(switchMap( () => {
      this.logger.debug('New Values!');

      const t = this.configuration.topic;
      return from(Topic.findOne({where: {id: t.id}, relations: ['contact', 'contact.elements', 'contact.image']}));
    }));
  }

  getContact(contactId: number): Observable<ContactInformation> {
    return from(ContactInformation.findOne({where: {id: contactId}, relations: ['elements']}));
  }

  async getPushMoments(): Promise<Moment[]> {
    const days = this.configuration.daysUntilOperation;

    return Moment.find({where: {push: true, start_time: MoreThanOrEqual(days)}});
  }


}
